import CheckIcon from '@mui/icons-material/Check';
import { Step, StepConnector, StepLabel, Stepper, stepConnectorClasses, useMediaQuery, useTheme } from '@mui/material';
import { StepIconProps } from '@mui/material/StepIcon';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useContext } from 'react';
import { BookingStore } from '../../utils/stores/BookingStore';
import { gradients } from '../../utils/theme';

const TRACK_COLOR = '#e4e4ec';

const Connector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 17,
        left: 'calc(-50% + 18px)',
        right: 'calc(50% + 18px)',
        [theme.breakpoints.down('sm')]: {
            top: 14,
            left: 'calc(-50% + 15px)',
            right: 'calc(50% + 15px)',
        },
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        backgroundImage: gradients.main,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        backgroundImage: gradients.main,
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        borderRadius: 2,
        backgroundColor: TRACK_COLOR,
        transition: 'background 0.4s ease',
    },
}));

const StepIconRoot = styled('div')(({ theme }) => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: TRACK_COLOR,
    color: '#9a9aa7',
    fontSize: 15,
    fontWeight: 600,
    zIndex: 1,
    transition: 'transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        width: 30,
        height: 30,
        fontSize: 13,
    },
    '&.active': {
        backgroundImage: gradients.circular,
        color: theme.palette.primary.contrastText,
        boxShadow: '0 4px 12px rgba(247, 97, 0, 0.4)',
        transform: 'scale(1.08)',
    },
    '&.completed': {
        backgroundImage: gradients.circular,
        color: theme.palette.primary.contrastText,
    },
}));

function BookingStepIcon({ active, completed, icon }: StepIconProps) {
    return (
        <StepIconRoot className={clsx({ active, completed })}>
            {completed ? <CheckIcon style={{ fontSize: 20 }} /> : icon}
        </StepIconRoot>
    );
}

interface BookingStepperProps {
    numberOfSteps: number;
}

const LargeStepper = ({ numberOfSteps }: BookingStepperProps) => {
    const bookingController = useContext(BookingStore);
    const currentStep = bookingController.step;
    const theme = useTheme();
    const hideLabels = useMediaQuery(theme.breakpoints.down('sm'));

    const stepLabels =
        numberOfSteps === 4
            ? [
                  bookingController.booking.isPackage ? 'Tour & Contact' : 'Lead Customer',
                  'Details',
                  'Meeting Point',
                  'Review & Pay',
              ]
            : [
                  bookingController.booking.isPackage ? 'Tour & Contact' : 'Lead Customer',
                  'Details',
                  'Extras',
                  'Meeting Point',
                  'Review & Pay',
              ];

    return (
        <Stepper
            alternativeLabel
            activeStep={currentStep}
            connector={<Connector />}
            sx={{ background: 'transparent', padding: { xs: '16px 0 24px', sm: '20px 0 30px' } }}
        >
            {stepLabels.map((label, i) => (
                <Step key={`step${i + 1}`}>
                    <StepLabel
                        StepIconComponent={BookingStepIcon}
                        sx={{
                            '& .MuiStepLabel-label': {
                                marginTop: { xs: '8px', sm: '10px' },
                                fontSize: { xs: 13, sm: 15 },
                                fontWeight: 500,
                                color: '#b6b6c0',
                                transition: 'color 0.3s ease, font-weight 0.3s ease',
                                '&.Mui-active': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                                '&.Mui-completed': {
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                            },
                        }}
                    >
                        {!hideLabels && label}
                    </StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

export default LargeStepper;
